import { Controller } from "@hotwired/stimulus";
import {
  CourseSelectionStore,
  type CourseData,
  type StorageChangeListener,
} from "./stores/course_selection_store";

export default class extends Controller {
  static targets = [
    "input",
    "selectedList",
    "items",
    "template",
    "slot",
    "placeholderTemplate",
    "selectionPanel",
  ];

  static values = {
    maxSlots: {
      type: Number,
      default: 3,
    },
  };

  // Store reference and listener binding
  private store: CourseSelectionStore = CourseSelectionStore.getInstance();
  private storeChangeListener: StorageChangeListener;

  private readonly slotConfig = {
    states: {
      OCCUPIED: "occupied",
      EMPTY: "empty",
    } as const,
  };

  declare readonly inputTargets: HTMLInputElement[];
  declare readonly selectedListTarget: HTMLElement;
  declare readonly itemsTarget: HTMLElement;
  declare readonly hasItemsTarget: boolean;
  declare readonly hasSelectedListTarget: boolean;
  declare readonly templateTarget: HTMLTemplateElement;
  declare readonly slotTargets: HTMLElement[];
  declare readonly placeholderTemplateTarget: HTMLTemplateElement;
  declare readonly maxSlotsValue: number;
  declare readonly selectionPanelTarget: HTMLElement;

  connect() {
    this.store.setMaxSlots(this.maxSlotsValue);
    this.store.initializeFromDOM(this.element);
    this.store.ensureProperStyling(this.element);

    // Bind store change listener
    this.storeChangeListener = this.onStoreChange.bind(this);
    this.store.addListener(this.storeChangeListener);

    // Initial sync of UI to store state
    this.syncSlotsFromStore();
  }

  disconnect() {
    // Clean up store listener
    this.store.removeListener(this.storeChangeListener);
  }

  toggle(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const courseData: CourseData = {
      id: checkbox.dataset.id,
      title: checkbox.dataset.title,
      imageSource: checkbox.dataset.imageSource,
    };

    const maxSlots = this.maxSlotsValue;

    if (checkbox.checked) {
      const success = this.store.addCourse(courseData, maxSlots);
      if (!success) {
        checkbox.checked = false;
        return;
      }
    } else {
      this.store.removeCourse(courseData.id);
    }
  }

  remove(event: Event) {
    const courseContent = (event.target as HTMLElement).closest(
      "[data-course-content]"
    ) as HTMLElement;
    const id = courseContent?.dataset.courseId;

    if (!id) return;

    this.store.removeCourse(id);

    // Dispatch a remove event for other controllers (like button_activate, button_swap)
    this.dispatch("remove");
  }

  private onStoreChange() {
    this.store.syncToDOM(document.body);
    this.syncSlotsFromStore();
  }

  /**
   * Synchronize the slot display with the current store state
   */
  private syncSlotsFromStore() {
    const selectedCourses = this.store.getSelectedCoursesArray();

    // Clear all slots first
    this.slotTargets.forEach((slot) => {
      this.restorePlaceholderContent(slot);
    });

    // Fill slots with selected courses
    selectedCourses.forEach((course, index) => {
      if (index < this.slotTargets.length) {
        this.populateSlot(this.slotTargets[index], course);
      }
    });
  }

  /**
   * Populate a slot with course data
   */
  private populateSlot(slot: HTMLElement, courseData: CourseData) {
    slot.innerHTML = "";

    const clone = this.templateTarget.content.cloneNode(
      true
    ) as DocumentFragment;

    const imageContainer = clone.querySelector(
      "[data-course-picker-target='image']"
    ) as HTMLImageElement | null;

    if (imageContainer) {
      imageContainer.src = courseData.imageSource;
    }

    // Store the course ID for removal
    const courseContent = clone.querySelector(
      "[data-course-content]"
    ) as HTMLElement | null;
    if (courseContent) {
      courseContent.dataset.courseId = courseData.id;
    }

    const button = clone.querySelector(
      "[data-course-picker-target='removeButton']"
    ) as HTMLButtonElement | null;

    if (button) {
      button.setAttribute("aria-label", `Remove ${courseData.title}`);
    }

    slot.appendChild(clone);
    this.setSlotState(slot, this.slotConfig.states.OCCUPIED);
  }

  private restorePlaceholderContent(slot: HTMLElement) {
    slot.innerHTML = "";

    const placeholderClone = this.placeholderTemplateTarget.content.cloneNode(
      true
    ) as DocumentFragment;

    const slotNumberElement = placeholderClone.querySelector(
      "[data-placeholder-slot-number]"
    );
    if (slotNumberElement && slot.dataset.slotNumber) {
      slotNumberElement.textContent = slot.dataset.slotNumber;
    }

    slot.appendChild(placeholderClone);
    this.setSlotState(slot, this.slotConfig.states.EMPTY);
  }

  private setSlotState(slot: HTMLElement, state: "occupied" | "empty"): void {
    slot.dataset.slotState = state;
  }
}
